import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const detailMessage = (err, fallback) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return d.message;
  if (Array.isArray(d)) return d.map((e) => e.msg).join(' ');
  return fallback;
};

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [state, setState] = useState({ phase: 'checking' });
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setState({ phase: 'invalid', message: 'This reset link is not valid.' }); return; }
    axios.get(`${API}/auth/reset-password/${encodeURIComponent(token)}`)
      .then((res) => setState({ phase: 'ready', name: res.data.name, expiresAt: res.data.expires_at }))
      .catch((err) => setState({ phase: 'invalid', message: detailMessage(err, 'This reset link is not valid.') }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw !== pw2) { setError('Passwords do not match.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setState({ phase: 'done' });
    } catch (err) {
      const code = err.response?.data?.detail?.code;
      if (code) setState({ phase: 'invalid', message: detailMessage(err, '') });
      else setError(detailMessage(err, 'Could not reset password.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md" data-testid="reset-password-page">
        <CardHeader>
          <CardTitle>Set a new password</CardTitle>
          {state.phase === 'ready' && <CardDescription data-testid="reset-page-for">
            For {state.name} · link expires {new Date(state.expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </CardDescription>}
        </CardHeader>
        <CardContent className="space-y-4">
          {state.phase === 'checking' && <p className="text-sm text-slate-600">Checking link…</p>}
          {state.phase === 'invalid' && <div className="space-y-3" data-testid="reset-link-error">
            <p className="text-sm text-red-700 font-medium">{state.message}</p>
            <p className="text-sm text-slate-600">Ask an administrator to send you a new reset link.</p>
          </div>}
          {state.phase === 'done' && <p className="text-sm text-green-800 font-medium" data-testid="reset-success">
            Password updated. You can now log in with your new password.
          </p>}
          {state.phase === 'ready' && <form className="space-y-3" onSubmit={submit}>
            <div className="space-y-1">
              <Label htmlFor="reset-pw">New password</Label>
              <Input id="reset-pw" type="password" required minLength={8} data-testid="reset-new-password"
                value={pw} onChange={(e) => setPw(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="reset-pw2">Confirm new password</Label>
              <Input id="reset-pw2" type="password" required minLength={8} data-testid="reset-confirm-password"
                value={pw2} onChange={(e) => setPw2(e.target.value)} />
            </div>
            <p className="text-xs text-slate-500">At least 8 characters. Common passwords are not allowed.</p>
            {error && <p className="text-sm text-red-700" data-testid="reset-form-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={saving} data-testid="reset-submit-btn">
              {saving ? 'Saving…' : 'Set new password'}
            </Button>
          </form>}
          <Link to="/login" className="block text-sm text-blue-700 underline" data-testid="reset-back-to-login">Back to login</Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
